// Pure helpers for the organiser assignment workflow. Framework-free so they
// can be unit-tested and reused by both the /beheer table and the
// /beheer/toewijzen werkbank.

export type QueueReg = {
  buurtbewoner: boolean
  inschrijvingsdatum: string | null
  naam: string
}

// Assignment priority: buurtbewoners first, then oldest registration first
// (ISO date strings compare chronologically), nulls last, then by name.
export function sortForAssignment<T extends QueueReg>(regs: T[]): T[] {
  return [...regs].sort((a, b) => {
    if (a.buurtbewoner !== b.buurtbewoner) return a.buurtbewoner ? -1 : 1
    const da = a.inschrijvingsdatum
    const db = b.inschrijvingsdatum
    if (da && db) {
      if (da !== db) return da < db ? -1 : 1
    } else if (da) {
      return -1
    } else if (db) {
      return 1
    }
    return a.naam.localeCompare(b.naam)
  })
}

// Combining diacritical marks (U+0300–U+036F), built via RegExp so the source
// stays plain ASCII.
const DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g')

function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

// Street-level match between a registrant's free-text "gewenst adres" and a
// polygon's zone / street address. Used to highlight likely spots (their gevel
// or preferred street) while the organiser assigns. Deliberately loose — a
// hint, not a hard filter.
export function matchesGewenstAdres(
  spot: { zone: string; streetAddress: string },
  gewenstAdres: string,
): boolean {
  const g = norm(gewenstAdres)
  if (!g) return false
  const zone = norm(spot.zone)
  if (zone && g.includes(zone)) return true
  // Street name = the streetAddress with the house number (and anything after)
  // stripped, e.g. "Fluweelstraat 34" -> "fluweelstraat".
  const streetName = norm(spot.streetAddress).replace(/\s*\d.*$/, '').trim()
  if (streetName && g.includes(streetName)) return true
  return false
}
